import { Link } from "react-router-dom";
import { EyeSlash, Flag, IdentificationCard, ImageSquare, LockKey, Prohibit, Scales, SealCheck, Siren, UsersThree } from "@phosphor-icons/react";
import { useSeo, useSite } from "@/hooks/useSite";
import { Heading, IconTile, PageHero, SitePage } from "@/components/site/SiteBits";

const ICONS = [SealCheck, IdentificationCard, Prohibit, UsersThree, Flag, LockKey, ImageSquare, EyeSlash, Scales];
const LINKS = [["community guidelines", "/guidelines"], ["contact support", "/support"]];

// Turn known phrases in Content text into links (text itself stays from Content).
function Linked({ text }) {
  if (!text) return null;
  const re = new RegExp(`(${LINKS.map(([p]) => p).join("|")})`, "gi");
  return text.split(re).map((part, i) => {
    const hit = LINKS.find(([p]) => p === part.toLowerCase());
    return hit ? <Link key={i} to={hit[1]} className="s-coral s-focus rounded font-semibold underline-offset-4 hover:underline">{part}</Link> : part;
  });
}

export default function SafetyPage() {
  const { data, error, loading, reload } = useSite();
  const f = data?.docs?.site_safety?.fields || {};
  const cards = data?.docs?.safety_page?.sections || [];
  const tips = data?.safety_tips || [];
  useSeo("Safety", f.hero_lead);
  return (
    <SitePage loading={loading} error={error} onRetry={reload} testId="safety-page">
      <PageHero chip={f.hero_chip} title={f.hero_title} accent={f.hero_accent} lead={f.hero_lead} testId="safety-hero" />
      <ul className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="safety-cards">
        {cards.map((c, i) => (
          <li key={i} className="s-card p-7" data-testid={`safety-card-${i}`}>
            <IconTile icon={ICONS[i % ICONS.length]} />
            <h2 className="mt-5 text-lg font-bold">{c.heading}</h2><p className="s-muted mt-2 leading-7">{c.body}</p>
          </li>))}
      </ul>
      {tips.length > 0 && (
        <section className="mt-24 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" data-testid="safety-tips">
          <div><Heading title={f.tips_title} accent={f.tips_accent} />
            {f.tips_foot && <p className="s-muted mt-5 leading-7" data-testid="safety-tips-foot"><Linked text={f.tips_foot} /></p>}</div>
          <ol className="space-y-4">
            {tips.map((t, i) => (
              <li key={i} className="s-card flex gap-4 p-5" data-testid={`safety-tip-${i + 1}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--s-grad)" }}>{i + 1}</span>
                <p className="pt-1.5 leading-7">{t}</p>
              </li>))}
          </ol>
        </section>)}
      {f.danger_title && (
        <section className="mt-20 flex flex-col gap-6 rounded-[24px] border border-[rgba(240,122,92,0.35)] bg-[rgba(240,122,92,0.1)] p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10" data-testid="safety-danger">
          <div className="flex gap-4"><IconTile icon={Siren} />
            <div><p className="s-eyebrow">{f.danger_eyebrow}</p><h2 className="mt-2 text-2xl font-bold">{f.danger_title}</h2>{f.danger_line && <p className="s-muted mt-2 leading-7">{f.danger_line}</p>}</div></div>
          {f.danger_button && <Link to="/support" className="s-btn s-btn-primary s-focus h-12 px-7" data-testid="safety-danger-button">{f.danger_button}</Link>}
        </section>)}
    </SitePage>
  );
}
